import { yupResolver } from "@hookform/resolvers/yup";
import { Alert, Button } from "@mui/material";
import { ActionButton, DialogModal } from "@zfp-akademie-a-s/zfp-design";
import { FormTextField } from "@zfp-akademie-a-s/zfp-design/form";
import { NoteIcon } from "Components/_Shared/Icons/Icons";
import { FeatureFlag, useFeatureFlag } from "Infrastructure/Hooks/useFeatureFlag";
import { type ResourcePathType, Resources, useResource } from "Infrastructure/Translations/Resources";
import { useContext, useEffect, useState } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { type ObjectSchema, object, string } from "yup";
import { ANALYSIS_NOTE_MAX_LENGTH } from "../constants";
import { AnalysisDetailContext } from "./AnalysisDetailContext";

type Props = {
  readonly content: string;
  readonly onSave: (content: string) => void;
  readonly readOnly?: boolean;
};

type InternalNoteFormType = {
  note?: string;
};

const getInternalNoteSchema: (t: (resourcePath: ResourcePathType, options?: any) => string) => ObjectSchema<InternalNoteFormType> = (t) => {
  return object({
    note: string().max(ANALYSIS_NOTE_MAX_LENGTH, t(Resources.Validation.String.Max, { count: ANALYSIS_NOTE_MAX_LENGTH })),
  }).defined();
};

export const InternalNoteModal = ({ content, onSave }: Props) => {
  const { t } = useResource();
  const { readOnly } = useContext(AnalysisDetailContext);
  const isAnalysisSharingEnabled = useFeatureFlag(FeatureFlag.AnalysisSharing);

  const [open, setOpen] = useState(false);

  const methods = useForm<InternalNoteFormType>({
    resolver: yupResolver(getInternalNoteSchema(t)),
    defaultValues: {
      note: content,
    },
  });

  const {
    control,
    handleSubmit,
    reset,
    formState: { errors },
  } = methods;

  useEffect(() => {
    if (open) {
      reset({ note: content });
    }
  }, [open, content, reset]);

  const handleClose = () => {
    setOpen(false);
  };

  const onSubmit = (data: InternalNoteFormType) => {
    onSave(data.note ?? "");
    setOpen(false);
  };

  return (
    <>
      <ActionButton label={t(Resources.AnalysisDetail.Note.Tooltip)} icon={<NoteIcon />} onClick={() => setOpen(true)} />
      <FormProvider {...methods}>
        <DialogModal
          open={open}
          onClose={handleClose}
          closeLabel={t(Resources.Common.Close)}
          title={t(Resources.AnalysisDetail.Note.Title)}
          subtitle={
            <Alert severity="info">
              {t(isAnalysisSharingEnabled ? Resources.AnalysisDetail.Note.SharedHint : Resources.AnalysisDetail.Note.Hint)}
            </Alert>
          }
          content={
            <FormTextField
              control={control}
              name="note"
              label={t(Resources.AnalysisDetail.Note.Title)}
              maxLength={ANALYSIS_NOTE_MAX_LENGTH}
              disabled={readOnly}
              multiline
              minRows={6}
              maxRows={8}
              fullWidth
            />
          }
          onSubmit={handleSubmit(onSubmit)}
          primaryAction={
            <Button variant="contained" disabled={readOnly || !!errors.note} type="submit">
              {t(Resources.Common.Save)}
            </Button>
          }
        />
      </FormProvider>
    </>
  );
};
